// AllPaySlips.jsx — Admin: Generate & View All Employees' Payslips for a Month
// Route: /pay-slips  (no employee_id in URL)
//
// Features:
//  - Month / Year selector (default = previous month)
//  - Employee dropdown → only shows employees who do NOT yet have a payslip
//    for the selected month/year (re-fetches on month/year change)
//  - Preview modal before generating (reuses same preview API)
//  - Bottom table shows ALL generated payslips for the selected month
//  - PDF print helper (no extra library)

import { useEffect, useState, useCallback, useRef } from "react";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Badge from "react-bootstrap/Badge";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

// ─── helpers ──────────────────────────────────────────────────────────────────
const MONTHS = [
  { v: 1, l: "January" },
  { v: 2, l: "February" },
  { v: 3, l: "March" },
  { v: 4, l: "April" },
  { v: 5, l: "May" },
  { v: 6, l: "June" },
  { v: 7, l: "July" },
  { v: 8, l: "August" },
  { v: 9, l: "September" },
  { v: 10, l: "October" },
  { v: 11, l: "November" },
  { v: 12, l: "December" },
];

const fmt = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const prevMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return { month: d.getMonth() + 1, year: d.getFullYear() };
};

// ─── PDF print helper ─────────────────────────────────────────────────────────
const printPayslip = (slip) => {
  if (!slip) return;
  const d = slip.payslip_data || {};
  const att = d.attendance || {};
  const MONTH_NAMES = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const html = `
<!DOCTYPE html><html><head><title>Payslip</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;margin:0;padding:20px}
  .header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:16px}
  .header h2{margin:0 0 4px;font-size:18px}.header p{margin:0;font-size:11px}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 20px;margin-bottom:16px}
  .info-grid div{font-size:11px} .info-grid strong{display:inline-block;width:130px}
  .section-title{background:#f0f0f0;padding:4px 8px;font-weight:bold;margin:12px 0 6px;font-size:12px}
  table{width:100%;border-collapse:collapse;margin-bottom:12px}
  th,td{border:1px solid #ddd;padding:5px 8px;font-size:11px}
  th{background:#f5f5f5;font-weight:bold} .text-right{text-align:right}
  .att-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:12px}
  .att-box{border:1px solid #ddd;padding:6px;text-align:center;border-radius:4px}
  .att-box .num{font-size:18px;font-weight:bold;color:#663399}
  .att-box .lbl{font-size:10px;color:#666}
  .total-row{background:#f9f9f9;font-weight:bold}
  .net-box{background:#663399;color:#fff;padding:10px;text-align:center;border-radius:6px;margin-top:10px}
  .net-box .net-lbl{font-size:11px;opacity:.85} .net-box .net-val{font-size:20px;font-weight:bold}
  @media print{body{padding:10px}}
</style></head><body>
  <div class="header">
    <h2>${d.company_name || slip.company_name || "Company Name"}</h2>
    <p>${d.company_address || slip.company_address || ""}</p>
    <p style="font-size:14px;font-weight:bold;margin-top:6px">
      PAY SLIP — ${MONTH_NAMES[d.salary_month || slip.salary_month || 1]} ${d.salary_year || slip.salary_year || ""}
    </p>
  </div>
  <div class="info-grid">
    <div><strong>Employee Name:</strong> ${d.employee_name || slip.employee_name || "-"}</div>
    <div><strong>Staff ID:</strong> ${d.staff_id || slip.staff_id || "-"}</div>
    <div><strong>Designation:</strong> ${d.designation_name || slip.designation_name || "-"}</div>
    <div><strong>Department:</strong> ${d.department_name || slip.department_name || "-"}</div>
    <div><strong>PF/UAN:</strong> ${d.pf_uan_number || slip.pf_uan_number || "-"}</div>
    <div><strong>ESIC:</strong> ${d.esic_ip_number || slip.esic_ip_number || "-"}</div>
    <div><strong>Payslip No:</strong> ${d.payslip_key || slip.payslip_key || "-"}</div>
    <div><strong>Generated On:</strong> ${slip.created_at ? new Date(slip.created_at).toLocaleDateString("en-IN") : "-"}</div>
  </div>
  <div class="section-title">Attendance Summary</div>
  <div class="att-grid">
    <div class="att-box"><div class="num">${att.working_days ?? "-"}</div><div class="lbl">Working Days</div></div>
    <div class="att-box"><div class="num">${att.present_days ?? "-"}</div><div class="lbl">Present (incl. half-day)</div></div>
    <div class="att-box"><div class="num" style="color:#dc3545">${att.absent_days ?? "-"}</div><div class="lbl">Absent (Deducted)</div></div>
    <div class="att-box"><div class="num">${att.leave_days ?? "-"}</div><div class="lbl">Paid Leave</div></div>
  </div>
  <table>
    <thead><tr><th>Earnings</th><th class="text-right">Amount</th><th>Deductions</th><th class="text-right">Amount</th></tr></thead>
    <tbody>
      ${(() => {
        const sal = d.salary || {};
        const earn = sal.earnings || [];
        const dedu = sal.deductions || [];
        const max = Math.max(earn.length, dedu.length);
        let rows = "";
        for (let i = 0; i < max; i++) {
          const e = earn[i] || {};
          const dd = dedu[i] || {};
          rows += `<tr>
            <td>${e.component || ""}</td><td class="text-right">${e.component ? fmt(e.amount) : ""}</td>
            <td>${dd.component || ""}</td><td class="text-right">${dd.component ? fmt(dd.amount) : ""}</td>
          </tr>`;
        }
        return rows;
      })()}
      <tr class="total-row">
        <td>Gross Earnings</td><td class="text-right">${fmt(d.salary?.payable_gross || d.gross_salary)}</td>
        <td>Total Deductions</td><td class="text-right">${fmt(d.salary?.total_deductions || d.policy_deductions)}</td>
      </tr>
      ${att.absent_days > 0 ? `<tr><td></td><td></td><td style="color:#dc3545">Absence Deduction (${att.absent_days} day${att.absent_days > 1 ? "s" : ""})</td><td class="text-right" style="color:#dc3545">- ${fmt(d.salary?.absence_deduction || 0)}</td></tr>` : ""}
    </tbody>
  </table>
  <div class="net-box">
    <div class="net-lbl">Net Salary Payable</div>
    <div class="net-val">${fmt(d.salary?.net_salary || d.net_salary)}</div>
  </div>
  <p style="font-size:10px;color:#999;margin-top:16px;text-align:center">
    This is a computer-generated payslip. No signature required.
  </p>
</body></html>`;

  const win = window.open("", "_blank", "width=800,height=650");
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
  };
};

// =============================================================================
export default function AllPaySlips() {
  const def = prevMonth();
  const [selMonth, setSelMonth] = useState(def.month);
  const [selYear, setSelYear] = useState(def.year);

  // employees without a payslip for the selected month
  const [unpaidEmployees, setUnpaidEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [selEmployees, setSelEmployees] = useState([]); // array of selected employee ids
  const [empDropdownOpen, setEmpDropdownOpen] = useState(false); // dropdown visibility
  const empDropdownRef = useRef(null);

  // filters
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterEmployeeName, setFilterEmployeeName] = useState("");
  const [filterDesignation, setFilterDesignation] = useState("");

  // filter options
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  // all payslips for selected month
  const [payslips, setPayslips] = useState([]);
  const [psLoading, setPsLoading] = useState(false);

  // preview modal
  const [showModal, setShowModal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState("");

  // delete confirm
  const [deleteId, setDeleteId] = useState(null);

  // status toggle
  const [togglingId, setTogglingId] = useState(null);

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  // ── fetch filter options (departments, designations) ───────────────────────
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [deptRes, desRes] = await Promise.all([
        api.get("/api/departments"),
        api.get("/api/designations"),
      ]);
      setDepartments(deptRes.data?.departments || []);
      setDesignations(desRes.data?.designations || []);
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  }, []);

  // ── fetch employees who have NO payslip for this month/year ─────────────────
  // Route: GET /api/payslips/available-employees?month=X&year=Y
  const fetchUnpaidEmployees = useCallback(async () => {
    setEmpLoading(true);
    setSelEmployees([]); // reset picker on month/year change
    try {
      const res = await api.get("/api/payslips/available-employees", {
        params: {
          month: selMonth,
          year: selYear,
          department_id: filterDepartment || undefined,
          employee_name: filterEmployeeName || undefined,
          designation_id: filterDesignation || undefined,
        },
      });
      setUnpaidEmployees(res.data?.data || []);
    } catch {
      setUnpaidEmployees([]);
    } finally {
      setEmpLoading(false);
    }
  }, [
    selMonth,
    selYear,
    filterDepartment,
    filterEmployeeName,
    filterDesignation,
  ]);

  // ── handle click outside to close dropdown ──────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        empDropdownRef.current &&
        !empDropdownRef.current.contains(event.target)
      ) {
        setEmpDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── toggle employee selection ───────────────────────────────────────────────
  const toggleEmployee = (empId) => {
    setSelEmployees((prev) =>
      prev.includes(String(empId))
        ? prev.filter((id) => id !== String(empId))
        : [...prev, String(empId)],
    );
  };

  // ── select all employees ─────────────────────────────────────────────────────
  const selectAllEmployees = () => {
    if (selEmployees.length === unpaidEmployees.length) {
      setSelEmployees([]);
    } else {
      setSelEmployees(unpaidEmployees.map((e) => String(e.id)));
    }
  };

  // ── fetch ALL payslips for this month/year ───────────────────────────────────
  // Route: GET /api/payslips/by-month?month=X&year=Y
  // (separate from the employee-specific GET /api/payslips?employee_id=X)
  const fetchMonthPayslips = useCallback(async () => {
    setPsLoading(true);
    try {
      const res = await api.get("/api/payslips/by-month", {
        params: {
          month: selMonth,
          year: selYear,
          department_id: filterDepartment || undefined,
          employee_name: filterEmployeeName || undefined,
          designation_id: filterDesignation || undefined,
        },
      });
      setPayslips(res.data?.data || []);
    } catch {
      setPayslips([]);
    } finally {
      setPsLoading(false);
    }
  }, [
    selMonth,
    selYear,
    filterDepartment,
    filterEmployeeName,
    filterDesignation,
  ]);

  useEffect(() => {
    fetchFilterOptions();
    fetchUnpaidEmployees();
    fetchMonthPayslips();
  }, [fetchFilterOptions, fetchUnpaidEmployees, fetchMonthPayslips]);

  // ── open preview modal for single employee ──────────────────────────────────
  const openPreview = async (employeeId) => {
    if (!employeeId) return;
    setPreviewing(true);
    setGenMsg("");
    try {
      const res = await api.get("/api/payslips/preview", {
        params: { employee_id: employeeId, month: selMonth, year: selYear },
      });

      console.log("Preview data:", res.data);
      setPreview({ ...res.data, employeeId });
      setShowModal(true);
    } catch (err) {
      setGenMsg(err.response?.data?.message || "Failed to load preview.");
    } finally {
      setPreviewing(false);
    }
  };

  // ── confirm generate single payslip ─────────────────────────────────────────
  const confirmGenerate = async () => {
    setGenerating(true);
    setGenMsg("");
    try {
      await api.post("/api/payslips/generate", {
        employee_id: preview?.employeeId,
        month: selMonth,
        year: selYear,
      });
      // Remove generated employee from selection
      setSelEmployees((prev) =>
        prev.filter((id) => id !== String(preview?.employeeId)),
      );
      setShowModal(false);
      setPreview(null);
      // refresh both lists
      await fetchUnpaidEmployees();
      await fetchMonthPayslips();
    } catch (err) {
      setGenMsg(err.response?.data?.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  // ── bulk generate payslips for selected employees ───────────────────────────
  const bulkGenerate = async () => {
    if (selEmployees.length === 0) return;
    setGenerating(true);
    setGenMsg("");
    let successCount = 0;
    let failCount = 0;

    for (const empId of selEmployees) {
      try {
        await api.post("/api/payslips/generate", {
          employee_id: empId,
          month: selMonth,
          year: selYear,
        });
        successCount++;
      } catch {
        failCount++;
      }
    }

    setGenMsg(
      `${successCount} payslip(s) generated successfully.${failCount > 0 ? ` ${failCount} failed.` : ""}`,
    );
    setSelEmployees([]);
    setGenerating(false);
    await fetchUnpaidEmployees();
    await fetchMonthPayslips();
  };

  // ── delete ────────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/api/payslips/${deleteId}`);
      setDeleteId(null);
      await fetchUnpaidEmployees();
      await fetchMonthPayslips();
    } catch {
      alert("Delete failed.");
    }
  };

  // ── toggle status (paid/unpaid) ───────────────────────────────────────────────
  const toggleStatus = async (payslipId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    setTogglingId(payslipId);
    try {
      await api.put(`/api/payslips/${payslipId}/status`, { status: newStatus });
      // Update UI optimistically
      setPayslips((prev) =>
        prev.map((p) =>
          p.id === payslipId
            ? {
                ...p,
                status: newStatus,
                statusLabel: newStatus === 1 ? "Paid" : "Generated",
              }
            : p,
        ),
      );
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  // ── download PDF ──────────────────────────────────────────────────────────────
  const handleDownload = async (row) => {
    try {
      const res = await api.get(`/api/payslips/${row.id}`);
      printPayslip(res.data);
    } catch {
      alert("Could not load payslip details.");
    }
  };

  // ── selected employees labels ───────────────────────────────────────────────
  const getEmpLabel = (empId) => {
    const emp = unpaidEmployees.find((e) => String(e.id) === String(empId));
    return (
      emp?.name ||
      `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim() ||
      `Employee #${empId}`
    );
  };
  const MONTH_NAME = MONTHS.find((m) => m.v === selMonth)?.l || "";

  // ─── Preview Modal ────────────────────────────────────────────────────────────
  const PreviewModal = () => {
    if (!preview) return null;
    const att = preview.attendance || {};
    const sal = preview.salary_structure || {};

    return (
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ background: "#663399", color: "#fff" }}
        >
          <Modal.Title style={{ fontSize: 16 }}>
            Payslip Preview —{" "}
            {preview?.employeeId ? getEmpLabel(preview.employeeId) : ""} |{" "}
            {MONTH_NAME} {selYear}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>
          {preview.alreadyGenerated && (
            <div
              className="alert alert-warning py-2 mb-3"
              style={{ fontSize: 13 }}
            >
              ⚠️ A payslip already exists for this month (ID #
              {preview.existing_payslip_id}). Generating again is blocked.
            </div>
          )}

          {/* Attendance */}
          <h6 className="fw-bold mb-2" style={{ color: "#663399" }}>
            Attendance
          </h6>
          <div className="row g-2 mb-3">
            {[
              {
                label: "Total Days in Month",
                val: att.total_working_days,
                color: "#333",
              },
              {
                label: "Working Days (excl. weekend)",
                val: att.working_days,
                color: "#17a2b8",
              },
              {
                label: "Present Days",
                val: att.regular_present,
                color: "#198754",
              },
              {
                label: "Approved Leave Days",
                val: att.approved_leave_days,
                color: "#0d6efd",
              },
              {
                label: "Weekend Days",
                val: att.weekend_days,
                color: "#6c757d",
              },
              {
                label: "Total Present (incl. leave)",
                val: att.present_days,
                color: "#198754",
              },
              { label: "Absent Days", val: att.absent_days, color: "#dc3545" },
              {
                label: "Bonus Amount",
                val: att.bonus_amount,
                color: "#ffc107",
                isCurrency: true,
              },
            ].map(({ label, val, color, isCurrency }) => (
              <div key={label} className="col-4">
                <div
                  className="border rounded p-2 text-center"
                  style={{ fontSize: 12 }}
                >
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>
                    {isCurrency ? fmt(val) : (val ?? 0)}
                  </div>
                  <div style={{ color: "#666" }}>{label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Earnings */}
          <h6 className="fw-bold mb-2" style={{ color: "#663399" }}>
            Earnings
          </h6>
          <table
            className="table table-sm table-bordered mb-3"
            style={{ fontSize: 12 }}
          >
            <tbody>
              {(sal.earnings || []).map((e, i) => (
                <tr key={i}>
                  <td>{e.component}</td>
                  <td className="text-end">{fmt(e.amount)}</td>
                </tr>
              ))}
              <tr className="fw-bold table-light">
                <td>Gross Salary</td>
                <td className="text-end">{fmt(sal.payable_gross)}</td>
              </tr>
            </tbody>
          </table>

          {/* Deductions */}
          <h6 className="fw-bold mb-2" style={{ color: "#663399" }}>
            Deductions
          </h6>
          <table
            className="table table-sm table-bordered mb-3"
            style={{ fontSize: 12 }}
          >
            <tbody>
              {(sal.deductions || []).map((d, i) => (
                <tr key={i}>
                  <td>{d.component}</td>
                  <td className="text-end">{fmt(d.amount)}</td>
                </tr>
              ))}
              {att.absent_days > 0 && (
                <tr style={{ color: "#dc3545" }}>
                  <td>
                    Absence Deduction ({att.absent_days} day
                    {att.absent_days > 1 ? "s" : ""})
                  </td>
                  <td className="text-end">
                    {fmt(sal.absence_deduction || 0)}
                  </td>
                </tr>
              )}
              <tr className="fw-bold table-light">
                <td>Total Deductions</td>
                <td className="text-end">
                  {fmt(
                    (sal.total_deductions || 0) + (sal.absence_deduction || 0),
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Net */}
          <div
            className="rounded p-3 text-center text-white"
            style={{ background: "#663399" }}
          >
            <div style={{ fontSize: 12, opacity: 0.85 }}>
              Net Salary Payable
            </div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {fmt(sal.net_salary)}
            </div>
          </div>

          {genMsg && (
            <div
              className="alert alert-danger mt-2 py-2 mb-0"
              style={{ fontSize: 12 }}
            >
              {genMsg}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
          {!preview.alreadyGenerated && (
            <Button
              size="sm"
              disabled={generating}
              onClick={confirmGenerate}
              style={{ background: "#663399", border: "none", minWidth: 150 }}
            >
              {generating ? "Generating…" : "✅ Confirm & Generate"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  };

  // ─── Delete confirm modal ─────────────────────────────────────────────────────
  const DeleteModal = () => (
    <Modal
      show={!!deleteId}
      onHide={() => setDeleteId(null)}
      centered
      size="sm"
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 15 }}>Delete Payslip</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        Are you sure you want to delete this payslip? This cannot be undone.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={() => setDeleteId(null)}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={confirmDelete}>
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // ─── Main render ──────────────────────────────────────────────────────────────
  return (
    <section>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Breadcrumb
          routeSegments={[
            { name: "Dashboard", path: "/" },
            { name: "Pay Slips" },
          ]}
        />
      </div>

      {/* ── Controls card ── */}
      <Card body className="mb-3">
        <div className="d-flex flex-wrap align-items-end gap-3">
          {/* Month */}
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Month
            </label>
            <select
              className="form-select form-select-sm"
              value={selMonth}
              onChange={(e) => setSelMonth(Number(e.target.value))}
              style={{ width: 140 }}
            >
              {MONTHS.map((m) => (
                <option key={m.v} value={m.v}>
                  {m.l}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Year
            </label>
            <select
              className="form-select form-select-sm"
              value={selYear}
              onChange={(e) => setSelYear(Number(e.target.value))}
              style={{ width: 100 }}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Department Filter */}
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Department
            </label>
            <select
              className="form-select form-select-sm"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">All Departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.department_name}
                </option>
              ))}
            </select>
          </div>

          {/* Designation Filter */}
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Designation
            </label>
            <select
              className="form-select form-select-sm"
              value={filterDesignation}
              onChange={(e) => setFilterDesignation(e.target.value)}
              style={{ width: 180 }}
            >
              <option value="">All Designations</option>
              {designations.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.designation_name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee Name Filter */}
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Employee Name
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by name..."
              value={filterEmployeeName}
              onChange={(e) => setFilterEmployeeName(e.target.value)}
              style={{ width: 180 }}
            />
          </div>

          {/* Clear Filters */}
          {(filterDepartment || filterDesignation || filterEmployeeName) && (
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => {
                setFilterDepartment("");
                setFilterDesignation("");
                setFilterEmployeeName("");
              }}
              style={{ height: 34 }}
            >
              Clear Filters
            </Button>
          )}

          {/* Employee Multi-Select Dropdown */}
          <div ref={empDropdownRef} style={{ position: "relative" }}>
            <label
              className="form-label mb-1"
              style={{ fontSize: 12, fontWeight: 600 }}
            >
              Available Employees ghhhhhhh — No payslip for {MONTH_NAME}{" "}
              {selYear}
              {empLoading && (
                <span
                  className="spinner-border spinner-border-sm ms-2"
                  style={{
                    color: "#663399",
                    width: 12,
                    height: 12,
                    borderWidth: 2,
                  }}
                />
              )}
            </label>
            <div
              className="form-select form-select-sm"
              onClick={() =>
                !empLoading && setEmpDropdownOpen(!empDropdownOpen)
              }
              style={{
                minWidth: 280,
                maxWidth: 350,
                cursor: empLoading ? "not-allowed" : "pointer",
                backgroundColor: empLoading ? "#e9ecef" : "#fff",
                userSelect: "none",
              }}
            >
              {empLoading
                ? "Loading…"
                : selEmployees.length === 0
                  ? unpaidEmployees.length === 0
                    ? "All employees paid for this month"
                    : `— Select employee(s) (${unpaidEmployees.length} available) —`
                  : `${selEmployees.length} employee${selEmployees.length > 1 ? "s" : ""} selected`}
            </div>

            {/* Dropdown Menu */}
            {empDropdownOpen && unpaidEmployees.length > 0 && (
              <div
                className="border rounded shadow-sm"
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1050,
                  background: "#fff",
                  maxHeight: 280,
                  overflowY: "auto",
                  marginTop: 2,
                }}
              >
                {/* Select All Header */}
                <div
                  className="px-3 py-2 border-bottom"
                  style={{
                    background: "#f8f9fa",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <label
                    className="d-flex align-items-center gap-2 mb-0"
                    style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selEmployees.length === unpaidEmployees.length &&
                        unpaidEmployees.length > 0
                      }
                      onChange={selectAllEmployees}
                      style={{ cursor: "pointer" }}
                    />
                    <span>Select All ({unpaidEmployees.length})</span>
                  </label>
                </div>

                {/* Employee List */}
                {unpaidEmployees.map((emp) => (
                  <label
                    key={emp.id}
                    className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                    style={{
                      cursor: "pointer",
                      fontSize: 13,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                  >
                    <input
                      type="checkbox"
                      checked={selEmployees.includes(String(emp.id))}
                      onChange={() => toggleEmployee(emp.id)}
                      style={{ cursor: "pointer" }}
                    />
                    <div className="d-flex flex-column">
                      <span className="fw-semibold" style={{ color: "#333" }}>
                        {emp.name ||
                          `${emp.first_name || ""} ${emp.last_name || ""}`.trim()}
                      </span>
                      <span style={{ fontSize: 11, color: "#666" }}>
                        {emp.staff_id
                          ? `Staff ID: ${emp.staff_id}`
                          : `ID: #${emp.id}`}
                        {emp.department_name && ` • ${emp.department_name}`}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Preview & Bulk Generate Buttons */}
          {selEmployees.length === 1 ? (
            <Button
              size="sm"
              disabled={previewing || empLoading}
              onClick={() => openPreview(selEmployees[0])}
              style={{
                background: "#663399",
                border: "none",
                height: 34,
                minWidth: 150,
              }}
            >
              {previewing ? "Loading…" : "🔍 Preview & Generate"}
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={selEmployees.length === 0 || generating || empLoading}
              onClick={bulkGenerate}
              style={{
                background: selEmployees.length > 0 ? "#663399" : "#adb5bd",
                border: "none",
                height: 34,
                minWidth: 150,
              }}
            >
              {generating
                ? "Generating…"
                : `⚡ Bulk Generate (${selEmployees.length})`}
            </Button>
          )}

          {genMsg && !showModal && (
            <span style={{ fontSize: 12, color: "#dc3545" }}>{genMsg}</span>
          )}
        </div>

        {/* Info pill about remaining unpaid count */}
        {!empLoading && (
          <div className="mt-2" style={{ fontSize: 12, color: "#6c757d" }}>
            {unpaidEmployees.length > 0 ? (
              <span>
                <span
                  className="badge rounded-pill me-1"
                  style={{ background: "#663399", fontSize: 11 }}
                >
                  {unpaidEmployees.length}
                </span>
                employee{unpaidEmployees.length > 1 ? "s" : ""} pending payslip
                for{" "}
                <strong>
                  {MONTH_NAME} {selYear}
                </strong>
              </span>
            ) : (
              <span>
                ✅ All employees have been paid for{" "}
                <strong>
                  {MONTH_NAME} {selYear}
                </strong>
              </span>
            )}
          </div>
        )}
      </Card>

      {/* ── All payslips table for selected month ── */}
      <Card body>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <Card.Title className="mb-0" style={{ fontSize: 15 }}>
            Pay Slips — {MONTH_NAME} {selYear}
          </Card.Title>
          <span style={{ fontSize: 12, color: "#6c757d" }}>
            {payslips.length} record{payslips.length !== 1 ? "s" : ""}
          </span>
        </div>

        {psLoading ? (
          <div className="text-center py-4">
            <div
              className="spinner-border spinner-border-sm"
              style={{ color: "#663399" }}
            />
            <p className="mt-2 text-muted" style={{ fontSize: 13 }}>
              Loading…
            </p>
          </div>
        ) : payslips.length === 0 ? (
          <p className="text-muted text-center py-3" style={{ fontSize: 13 }}>
            No payslips generated for {MONTH_NAME} {selYear}. Select an employee
            above to get started.
          </p>
        ) : (
          <div className="table-responsive">
            <table
              className="table table-hover align-middle"
              style={{ fontSize: 13 }}
            >
              <thead className="table-light">
                {/* Group header row */}
                <tr>
                  <th rowSpan={2}>#</th>
                  <th rowSpan={2}>Employee</th>
                  <th rowSpan={2}>Staff ID</th>
                  <th rowSpan={2}>Month</th>
                  <th rowSpan={2} className="text-center">
                    Present
                  </th>
                  <th
                    rowSpan={2}
                    className="text-center"
                    style={{ color: "#dc3545" }}
                  >
                    Absent
                  </th>
                  <th colSpan={4} className="text-center">
                    Earnings
                  </th>
                  <th colSpan={2} className="text-center">
                    Deductions
                  </th>
                  <th rowSpan={2} className="text-end">
                    Gross
                  </th>
                  <th
                    rowSpan={2}
                    className="text-end"
                    style={{ color: "#198754" }}
                  >
                    Net Salary
                  </th>
                  <th rowSpan={2} className="text-center">
                    Status
                  </th>
                  <th rowSpan={2} className="text-center">
                    Actions
                  </th>
                </tr>
                {/* Detail header row */}
                <tr>
                  <th className="text-end">Basic</th>
                  <th className="text-end">DA</th>
                  <th className="text-end">HRA</th>
                  <th className="text-end">Bonus</th>
                  <th className="text-end">PF</th>
                  <th className="text-end">Other Ded</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((ps, idx) => {
                  // Parse attendance data from stored JSON if available
                  let attendance = {};
                  try {
                    const otherPayments = ps.other_payments
                      ? JSON.parse(ps.other_payments)
                      : {};
                    attendance = otherPayments.attendance || {};
                  } catch (e) {
                    attendance = {};
                  }

                  // Get earnings from allowances column and deductions from deductions column
                  let earnings = [];
                  let deductions = [];
                  try {
                    earnings = ps.allowances ? JSON.parse(ps.allowances) : [];
                    deductions = ps.deductions ? JSON.parse(ps.deductions) : [];
                  } catch (e) {
                    earnings = [];
                    deductions = [];
                  }

                  // Helper to get component amount by name
                  const getComponentAmount = (components, name) => {
                    const comp = components.find((c) => c.component === name);
                    return comp ? comp.amount : 0;
                  };

                  // Get the first non-PF deduction component for "Other Ded"
                  const getOtherDeduction = (deductions) => {
                    const otherDed = deductions.find(
                      (d) => d.component !== "PF" && d.component !== "EPF",
                    );
                    return otherDed
                      ? { name: otherDed.component, amount: otherDed.amount }
                      : { name: "Other", amount: 0 };
                  };

                  return (
                    <tr key={ps.id}>
                      <td className="text-muted">{idx + 1}</td>
                      <td className="fw-semibold">
                        {ps.employee_name || `Employee #${ps.employee_id}`}
                      </td>
                      <td style={{ color: "#663399", fontWeight: 600 }}>
                        {ps.staff_id || "-"}
                      </td>
                      <td>{ps.salaryMonth}</td>
                      <td className="text-center">
                        {attendance.regular_present || ps.hours_worked || 0}
                      </td>
                      <td
                        className="text-center"
                        style={{ color: "#dc3545", fontWeight: 600 }}
                      >
                        {attendance.absent_days ?? 0}
                      </td>
                      <td className="text-end">
                        {fmt(getComponentAmount(earnings, "Basic"))}
                      </td>
                      <td className="text-end">
                        {fmt(getComponentAmount(earnings, "DA"))}
                      </td>
                      <td className="text-end">
                        {fmt(getComponentAmount(earnings, "HRA"))}
                      </td>
                      <td className="text-end">
                        {attendance.bonus_amount
                          ? fmt(attendance.bonus_amount)
                          : "—"}
                      </td>
                      <td className="text-end">
                        {fmt(getComponentAmount(deductions, "PF"))}
                      </td>
                      <td
                        className="text-end"
                        title={getOtherDeduction(deductions).name}
                      >
                        {fmt(getOtherDeduction(deductions).amount)}
                      </td>
                      <td className="text-end">
                        {`₹ ${Number(ps.gross_salary || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                      </td>
                      <td
                        className="text-end fw-bold"
                        style={{ color: "#198754" }}
                      >
                        {ps.netSalaryFmt}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          onClick={() => toggleStatus(ps.id, ps.status)}
                          disabled={togglingId === ps.id}
                          style={{
                            background: ps.status === 1 ? "#198754" : "#dc3545",
                            border: "none",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor:
                              togglingId === ps.id ? "not-allowed" : "pointer",
                            opacity: togglingId === ps.id ? 0.6 : 1,
                          }}
                        >
                          {togglingId === ps.id ? (
                            <>
                              <span style={{ marginRight: "4px" }}>⟳</span>
                              Updating...
                            </>
                          ) : ps.status === 1 ? (
                            <>
                              <span style={{ marginRight: "4px" }}>✓</span>
                              Paid
                            </>
                          ) : (
                            <>
                              <span style={{ marginRight: "4px" }}>✕</span>
                              Unpaid
                            </>
                          )}
                        </button>
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          {/* Download PDF */}
                          <button
                            className="btn btn-sm"
                            title="Download PDF"
                            onClick={() => handleDownload(ps)}
                            style={{
                              background: "#000000",
                              border: "none",
                              width: 30,
                              height: 30,
                              padding: 0,
                            }}
                          >
                            <i
                              className="i-File-Download text-white"
                              style={{ fontSize: 14 }}
                            />
                          </button>
                          {/* Delete */}
                          <button
                            className="btn btn-sm btn-danger"
                            title="Delete"
                            onClick={() => setDeleteId(ps.id)}
                            style={{ width: 30, height: 30, padding: 0 }}
                          >
                            <i
                              className="i-Close-Window text-white"
                              style={{ fontSize: 14 }}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Totals footer */}
              {payslips.length > 0 && (
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan={6} className="text-end">
                      Totals
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const earnings = p.allowances
                              ? JSON.parse(p.allowances)
                              : [];
                            const basic = earnings.find(
                              (e) => e.component === "Basic",
                            );
                            return s + Number(basic?.amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const earnings = p.allowances
                              ? JSON.parse(p.allowances)
                              : [];
                            const da = earnings.find(
                              (e) => e.component === "DA",
                            );
                            return s + Number(da?.amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const earnings = p.allowances
                              ? JSON.parse(p.allowances)
                              : [];
                            const hra = earnings.find(
                              (e) => e.component === "HRA",
                            );
                            return s + Number(hra?.amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const otherPayments = p.other_payments
                              ? JSON.parse(p.other_payments)
                              : {};
                            const attendance = otherPayments.attendance || {};
                            return s + Number(attendance.bonus_amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const deductions = p.deductions
                              ? JSON.parse(p.deductions)
                              : [];
                            const pf = deductions.find(
                              (d) => d.component === "PF",
                            );
                            return s + Number(pf?.amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce((s, p) => {
                          try {
                            const deductions = p.deductions
                              ? JSON.parse(p.deductions)
                              : [];
                            const other = deductions.find(
                              (d) =>
                                d.component !== "PF" && d.component !== "EPF",
                            );
                            return s + Number(other?.amount || 0);
                          } catch (e) {
                            return s;
                          }
                        }, 0),
                      )}
                    </td>
                    <td className="text-end">
                      {fmt(
                        payslips.reduce(
                          (s, p) => s + Number(p.gross_salary || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="text-end" style={{ color: "#198754" }}>
                      {fmt(
                        payslips.reduce(
                          (s, p) => s + Number(p.net_salary || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      <PreviewModal />
      <DeleteModal />
    </section>
  );
}
